import { EmailOutlined, PersonOutline, ShieldOutlined } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, MyModal, SelectInput, TextInput } from "components";
import { ENDPOINTS, MEMBER_ROLES } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useState } from "react";
import { MemberRole } from "types";
import { IRemoveMember } from "./types";

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

interface IEditMember extends IRemoveMember {
  onClose: () => void;
}

export default function EditMember({ onSuccess, onClose, member }: IEditMember) {
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();
  const [state, setState] = useState({
    name: member.user.name,
    email: member.user.email,
    role: member.role
  });

  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("PUT");
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.members + "/" + member._id, {
        body: JSON.stringify({
          role: state.role
        })
      }).catch(console.error);
    },
    [state.role, member, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
    }
  }, [response, onSuccess, onClose, enqueueSnackbar]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal title={t["Edit Member"]} open={true} setOpen={onClose} variant="light">
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            label={t["Email"]}
            value={state.email}
            disabled
            startAdornment={<EmailOutlined sx={iconSx} />}
            sx={fieldSx}
            fullWidth
          />
          <TextInput
            value={state.name}
            label={t["Name"]}
            type="text"
            disabled
            startAdornment={<PersonOutline sx={iconSx} />}
            sx={fieldSx}
            fullWidth
          />
          <SelectInput
            fullWidth
            data={MEMBER_ROLES}
            id="role-select"
            value={state.role}
            label={t["Role"]}
            helperText=""
            startAdornment={<ShieldOutlined sx={iconSx} />}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                role: e.target.value as MemberRole
              }));
            }}
          />
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Update"]}
          loading={isLoading}
        />
      </Box>
    </MyModal>
  );
}
